import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, PenLine, ChevronRight } from "lucide-react";
import { play, preload, sfx } from "../../lib/audio";
import { TraceCanvas } from "./TraceCanvas";

const TONES = [
  { t: 1, ch: "媽", zy: "ㄇㄚ", name: "一聲", shape: "M10 14 L90 14", color: "#EF4444" },
  { t: 2, ch: "麻", zy: "ㄇㄚˊ", name: "二聲", shape: "M10 46 L90 10", color: "#3B82F6" },
  { t: 3, ch: "馬", zy: "ㄇㄚˇ", name: "三聲", shape: "M10 22 Q50 70 90 14", color: "#10B981" },
  { t: 4, ch: "罵", zy: "ㄇㄚˋ", name: "四聲", shape: "M10 10 L90 46", color: "#8B5CF6" },
  { t: 5, ch: "嗎", zy: "˙ㄇㄚ", name: "輕聲", shape: "M48 30 L52 30", color: "#6B7280" },
];

const ToneCard = ({ tones }) => (
  <div className="flex flex-col items-center gap-6" data-testid="tone-card">
    <h2 className="font-brush text-5xl text-[var(--vermilion)]">聲調唱一唱</h2>
    <div className="flex flex-wrap justify-center gap-4">
      {TONES.filter((t) => tones.includes(t.t)).map((t) => (
        <motion.button key={t.t} data-testid={`tone-btn-${t.t}`} whileTap={{ scale: 0.9 }} onClick={() => play(t.ch)}
          className="scroll-card w-36 p-4 flex flex-col items-center gap-2" style={{ borderColor: t.color }}>
          <svg width="100" height="56" viewBox="0 0 100 56"><path d={t.shape} stroke={t.color} strokeWidth={t.t === 5 ? 14 : 7} strokeLinecap="round" fill="none" /></svg>
          <div className="font-serif-tc text-5xl font-black">{t.ch}</div>
          <div className="text-2xl font-bold" style={{ color: t.color }}>{t.zy}</div>
          <div className="text-sm text-stone-500">{t.name}</div>
        </motion.button>
      ))}
    </div>
  </div>
);

const SymbolCard = ({ z, onTraced }) => {
  const [trace, setTrace] = useState(false);
  useEffect(() => { setTrace(false); play(z.audio_text, z.audio_url); }, [z]);
  return (
    <div className="grid md:grid-cols-2 gap-6 items-center w-full max-w-5xl" data-testid={`zhuyin-card-${z.symbol}`}>
      <div className="flex flex-col items-center gap-4">
        <motion.button key={z.symbol} data-testid="zhuyin-big-symbol" initial={{ scale: 0.3, rotate: -10 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring" }}
          whileTap={{ scale: 0.9 }} onClick={() => play(z.audio_text, z.audio_url)}
          className="w-56 h-56 sm:w-64 sm:h-64 rounded-[2.5rem] bg-[#FFFDF7] border-4 border-amber-300 shadow-xl flex items-center justify-center text-[9rem] sm:text-[10rem] font-black text-amber-900 leading-none">
          {z.symbol}
        </motion.button>
        <div className="flex gap-3">
          <button data-testid="zhuyin-repeat-btn" onClick={() => play(z.audio_text, z.audio_url)} className="kid-btn px-5 bg-amber-100 text-[var(--tea)] flex items-center gap-2"><Volume2 /> 再聽</button>
          <button data-testid="zhuyin-trace-btn" onClick={() => setTrace(!trace)} className="kid-btn px-5 bg-[var(--jade)] text-white flex items-center gap-2"><PenLine /> 描一描</button>
        </div>
      </div>
      {trace ? (
        <TraceCanvas glyph={z.symbol} onComplete={onTraced} testid="zhuyin-trace-canvas" />
      ) : (
        <motion.button data-testid="zhuyin-example-btn" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}
          onClick={() => play(z.example_word)} className="scroll-card p-6 flex items-center justify-center gap-6">
          <span className="text-8xl">{z.image_url ? <img src={z.image_url} alt="" className="h-28" /> : z.image_asset}</span>
          <span className="flex items-center gap-2">
            <span className="font-serif-tc text-8xl font-black text-red-950">{z.example_character}</span>
            <span className="zhuyin-vert text-3xl font-bold text-[var(--vermilion)]">{z.example_zhuyin}</span>
          </span>
        </motion.button>
      )}
    </div>
  );
};

const ReviewGrid = ({ list }) => (
  <div className="flex flex-col items-center gap-6" data-testid="zhuyin-review-grid">
    <h2 className="font-brush text-5xl text-[var(--vermilion)]">三十七個注音朋友</h2>
    <div className="grid grid-cols-6 sm:grid-cols-8 lg:grid-cols-10 gap-3">
      {list.map((z, k) => (
        <motion.button key={z.symbol} data-testid={`review-zhuyin-${z.symbol}`} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: k * 0.02 }}
          whileTap={{ scale: 0.85 }} onClick={() => play(z.audio_text, z.audio_url)}
          className={`w-16 h-16 sm:w-20 sm:h-20 rounded-2xl text-4xl font-black shadow-md border-2 ${z.category === "initial" ? "bg-red-50 border-red-200 text-red-900" : z.category === "medial" ? "bg-green-50 border-green-200 text-green-900" : "bg-amber-50 border-amber-200 text-amber-900"}`}>
          {z.symbol}
        </motion.button>
      ))}
    </div>
  </div>
);

export const ZhuyinStep = ({ list, tones = [], reviewAll, onSeen, onTraced, onDone }) => {
  const pages = reviewAll ? ["grid"] : list.map((_, k) => k);
  if (tones.length) pages.push("tones");
  const [p, setP] = useState(0);
  useEffect(() => {
    preload(list.flatMap((z) => [z.audio_text, z.example_word]));
    onSeen(list.map((z) => ({ type: "zhuyin", key: z.symbol })));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const cur = pages[p];
  const next = () => { sfx("tap"); if (p < pages.length - 1) setP(p + 1); else onDone(); };
  return (
    <div className="flex flex-col items-center justify-between h-full gap-6 py-4" data-testid="zhuyin-step">
      <div className="flex-1 flex items-center justify-center w-full">
        {cur === "grid" ? <ReviewGrid list={list} /> : cur === "tones" ? <ToneCard tones={tones} /> : <SymbolCard z={list[cur]} onTraced={() => onTraced(list[cur].symbol)} />}
      </div>
      <button data-testid="zhuyin-next-btn" onClick={next} className="kid-btn px-10 bg-[var(--vermilion)] text-white text-2xl font-serif-tc flex items-center gap-2">
        {p < pages.length - 1 ? "下一個" : "學好了"} <ChevronRight />
      </button>
    </div>
  );
};
